import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import "../assets/css/ViewCustomer.css";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { getAuthItem } from "../utils/authStorage";

function ViewProjectSubscription({ subscriptionId, onBack }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const role = (() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();
  const canEdit = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);

  useEffect(() => {
    const load = async () => {
      if (!subscriptionId) return;
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/fico/project-subscription/${subscriptionId}`);
        const s = res?.data?.data ?? res?.data ?? null;
        setSubscription(s);
      } catch (err) {
        console.error("Error fetching project subscription:", err);
        alert("Failed to load subscription details.");
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [subscriptionId]);

  const InfoItem = ({ label, value }) => {
    const hasValue = value !== undefined && value !== null && String(value).trim() !== "" && value !== "—";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
    } catch {
      return "—";
    }
  };

  const formatAmount = (amount) => {
    if (amount == null || amount === "") return "—";
    const n = Number(String(amount).replace(/,/g, ""));
    if (Number.isNaN(n)) return String(amount);
    return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div>Loading subscription details...</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="viewlead-container">
        <div>Subscription not found.</div>
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
    );
  }

  const s = subscription;
  const projectId = s.Project_Id ?? s.project_id;

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>

      <div className="viewlead-card">
        <h3 className="viewlead-section-title" style={{ marginTop: 0 }}>Subscription Details</h3>

        {canEdit ? (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button
              type="button"
              className="btn-edit-followup"
              onClick={() => {
                const sid = s.Subscription_Id ?? s.subscription_id ?? s.id;
                if (sid != null && projectId != null) navigate(`/projects/${projectId}/edit?subscriptionId=${encodeURIComponent(String(sid))}`);
              }}
              title="Edit subscription"
            >
              <FaEdit />
            </button>
          </div>
        ) : null}

        <div className="viewlead-info-grid">
          <InfoItem label="Subscription ID" value={s.Subscription_Id ?? s.subscription_id ?? s.id ?? "—"} />
          <InfoItem label="Project" value={s.Project_Name ?? s.project_name ?? "—"} />
        </div>

        <h3 className="viewlead-section-title">Plan</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Plan Name" value={s.Plan_Name ?? s.plan_name ?? "—"} />
          <InfoItem label="Duration (days)" value={s.Duration_Days ?? s.duration_days ?? "—"} />
          <InfoItem label="Status" value={s.Status ?? s.status ?? "—"} />
          <InfoItem label="Amount" value={formatAmount(s.Amount ?? s.amount)} />
        </div>

        <h3 className="viewlead-section-title">Dates</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Start Date" value={formatDate(s.Start_Date ?? s.start_date)} />
          <InfoItem label="End Date" value={formatDate(s.End_Date ?? s.end_date)} />
        </div>

        <h3 className="viewlead-section-title">Audit</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Created By" value={s.Created_By_Name ?? s.created_by_name ?? s.Created_By ?? "—"} />
          <InfoItem label="Created On" value={formatDate(s.Created_On ?? s.created_on)} />
          <InfoItem label="Updated By" value={s.Updated_By_Name ?? s.updated_by_name ?? s.Updated_By ?? "—"} />
          <InfoItem label="Updated On" value={formatDate(s.Updated_On ?? s.updated_on)} />
        </div>

        {projectId ? (
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              className="btn-subproject-primary"
              onClick={() => {
                const refresh = Date.now();
                navigate(`/projects/${projectId}/view?refresh=${refresh}`);
              }}
            >
              Back to Project
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ViewProjectSubscription;

